// SPDX-License-Identifier: MIT
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';

export interface NextStepsModalOptions {
  // Optional callback fired when the user dismisses the modal (close or back).
  onClose?: () => void;
}

// Modal opened post-Copier on the project-preview-card. Closes the loop
// "Genesis generated my manifest, what now ?" by guiding the user explicitly
// to paste into Claude Code (CLI or web). Without this, users hit a dead end
// after the Copy button and bounce — observed empirically with the aurum.ai
// dogfood session 2026-05-16 ("après Magnify on est bloqué sans next step").
export function renderNextStepsModal(opts: NextStepsModalOptions = {}): HTMLElement {
  const el = document.createElement('div');
  el.className = 'next-steps-modal';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('data-component', 'next-steps-modal');
  el.innerHTML = `
    <div class="next-steps-modal__card">
      <button type="button" data-component="next-steps-close" class="next-steps-modal__close" aria-label="${escapeHtml(t('nextSteps.closeLabel'))}">×</button>
      <div class="next-steps-modal__icon" aria-hidden="true">✨</div>
      <h2 class="next-steps-modal__title">${escapeHtml(t('nextSteps.title'))}</h2>
      <p class="next-steps-modal__subtitle">${escapeHtml(t('nextSteps.subtitle'))}</p>

      <ol class="next-steps-modal__steps">
        <li>
          <span class="next-steps-modal__step-label">${escapeHtml(t('nextSteps.step1Title'))}</span>
          <span class="next-steps-modal__step-body">${escapeHtml(t('nextSteps.step1Body'))}</span>
        </li>
        <li>
          <span class="next-steps-modal__step-label">${escapeHtml(t('nextSteps.step2Title'))}</span>
          <span class="next-steps-modal__step-body">${escapeHtml(t('nextSteps.step2Body'))}</span>
        </li>
        <li>
          <span class="next-steps-modal__step-label">${escapeHtml(t('nextSteps.step3Title'))}</span>
          <span class="next-steps-modal__step-body">${escapeHtml(t('nextSteps.step3Body'))}</span>
        </li>
      </ol>

      <div class="next-steps-modal__actions">
        <a data-component="next-steps-claude-link" class="next-steps-modal__primary" href="https://claude.ai/new" target="_blank" rel="noopener noreferrer">${escapeHtml(t('nextSteps.openClaudeButton'))}</a>
        <button type="button" data-component="next-steps-back" class="next-steps-modal__secondary">${escapeHtml(t('nextSteps.backButton'))}</button>
      </div>

      <p class="next-steps-modal__hint">${escapeHtml(t('nextSteps.hint'))}</p>
    </div>
  `;

  const close = () => {
    if (el.parentElement) el.parentElement.removeChild(el);
    opts.onClose?.();
  };

  // Backdrop click (self-only) closes ; card click does not bubble.
  el.addEventListener('click', (e) => { if (e.target === el) close(); });
  el.querySelector<HTMLElement>('.next-steps-modal__card')!.addEventListener('click', (e) => e.stopPropagation());
  el.querySelector<HTMLElement>('[data-component="next-steps-close"]')!.addEventListener('click', close);
  el.querySelector<HTMLElement>('[data-component="next-steps-back"]')!.addEventListener('click', close);

  // ESC key to close.
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);

  return el;
}
